<template lang="html">
<div class="se-page">
    <header class="se-hero">
        <div>
            <div class="se-kicker">SMARTLINKS</div>
            <h1>Search Entities</h1>
        </div>
    </header>

    <section class="se-card">
        <label class="se-label">Search</label>
        <input
            v-model="query"
            type="text"
            class="se-input"
            placeholder="Global offer ID, affise ID or offer name"
            @keyup.enter="search"
        >


        <button type="button" class="se-btn se-btn--ok se-search-btn" :disabled="loading" @click="search">
            {{ loading ? 'Searching...' : 'Search' }}
        </button>
    </section>

    <div v-if="loading" class="se-loading">Loading...</div>

    <template v-else-if="searched">
        <section v-if="!hasResults" class="se-card">
            <p class="se-empty">Nothing matched "{{ appliedQuery }}".</p>
        </section>

        <section v-if="globalOffers.length > 0" class="se-card se-results">
            <div class="se-results-header">
                <h2>Global offers matching "{{ appliedQuery }}"</h2>
                <span class="se-badge">{{ globalOffers.length }} result(s)</span>
            </div>

            <table class="se-table">
                <thead>
                    <tr>
                        <th class="se-col-toggle"></th>
                        <th>ID</th>
                        <th>Offer name</th>
                        <th>Affise ID</th>
                        <th>Type</th>
                        <th>Offer page</th>
                    </tr>
                </thead>
                <tbody v-for="offer in globalOffers" :key="offerKey(offer)">
                    <tr class="se-row">
                        <td class="se-col-toggle">
                            <button
                                v-if="offer.items.length > 0"
                                type="button"
                                class="se-toggle"
                                :title="offer.open ? 'Hide Smartlinks' : 'Show Smartlinks'"
                                @click="offer.open = !offer.open"
                            >
                                <i
                                    class="fa fa-caret-down se-caret"
                                    :class="{ 'se-caret--open': offer.open }"
                                    aria-hidden="true"
                                ></i>
                            </button>
                        </td>
                        <td><span class="se-id">#{{ offer.id }}</span></td>
                        <td>{{ offer.name }}</td>
                        <td class="se-mono">{{ offer.affise_id }}</td>
                        <td><span class="se-badge">{{ offerTypeLabel(offer) }}</span></td>
                        <td>
                            <a class="se-link" :href="offerPageHref(offer)" target="_blank" rel="noopener">Offer/{{ offer.id }}</a>
                        </td>
                    </tr>

                    <tr v-if="offer.open && offer.items.length > 0">
                        <td colspan="6" class="se-usages-cell">
                            <div class="se-subcard">
                                <div class="se-subcard-title">Used by {{ offer.items.length }} ad set(s)</div>
                                <PausableOffersTable
                                    :items="offer.items"
                                    :busy="offer.busy"
                                    @pause="ids => pause(offer, ids)"
                                />
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </section>

        <section v-if="byAffise.items.length > 0" class="se-card se-results">
            <div class="se-results-header">
                <h2>Offers with affise ID matching "{{ appliedQuery }}"</h2>
                <span class="se-badge">{{ byAffise.items.length }} result(s)</span>
            </div>

            <PausableOffersTable
                :items="byAffise.items"
                :busy="byAffise.busy"
                @pause="ids => pause(byAffise, ids)"
            />
        </section>

        <section v-if="byName.items.length > 0" class="se-card se-results">
            <div class="se-results-header">
                <h2>Offers with a name matching "{{ appliedQuery }}"</h2>
                <span class="se-badge">{{ byName.items.length }} result(s)</span>
            </div>

            <PausableOffersTable
                :items="byName.items"
                :busy="byName.busy"
                @pause="ids => pause(byName, ids)"
            />
        </section>
    </template>
</div>
</template>

<script>
import PausableOffersTable from '../components/PausableOffersTable.vue'
import './search-entities.css'

export default {
    name: 'SearchEntities',
    components: { PausableOffersTable },
    data() {
        return {
            query: '',
            appliedQuery: '',
            loading: false,
            searched: false,
            globalOffers: [],
            byAffise: { items: [], busy: false },
            byName: { items: [], busy: false }
        }
    },
    computed: {
        hasResults() {
            return this.globalOffers.length > 0 || this.byAffise.items.length > 0 || this.byName.items.length > 0
        }
    },
    methods: {
        async search(e) {
            if (e) {
                e.preventDefault()
            }

            const query = this.query.trim()
            if (query === '') {
                this.notifyError('Enter a value to search.')
                return
            }

            this.loading = true
            try {
                const response = await this.$http.post(this.resources.SmartLinksCampaigns.searchEntities, { query })
                const result = await response.json()

                this.globalOffers = (result.global_offers || []).map(offer => Object.assign({}, offer, {
                    items: offer.usages || [],
                    open: true,
                    busy: false
                }))
                this.byAffise = { items: result.by_affise_id || [], busy: false }
                this.byName = { items: result.by_name || [], busy: false }
                this.appliedQuery = query
                this.searched = true
            } catch (err) {
                this.notifyError(err)
            } finally {
                this.loading = false
            }
        },
        async pause(entry, offerIds) {
            entry.busy = true
            try {
                const response = await this.$http.post(this.resources.SmartLinksCampaigns.pauseOfferWeights, {
                    offer_ids: offerIds
                })
                const result = await response.json()

                const paused = result.paused || []
                this.markPaused(paused)
                this.notifySuccess(`Paused ${paused.length} offer(s).`)

                const autoPaused = result.auto_paused_sls || []
                if (autoPaused.length > 0) {
                    this.notifySuccess(`Auto-paused Smartlink(s): ${autoPaused.join(', ')}`)
                }

                const skipped = result.skipped || []
                if (skipped.length > 0) {
                    this.notifyError(`Skipped ${skipped.length} offer(s): ${skipped[0].reason}`)
                }

                const failed = result.failed_sls || []
                if (failed.length > 0) {
                    this.notifyError(`Could not auto-pause Smartlink(s): ${failed.join(', ')}. Check them manually.`)
                }
            } catch (err) {
                this.notifyError(err)
            } finally {
                entry.busy = false
            }
        },
        markPaused(pausedIds) {
            const lists = [this.byAffise.items, this.byName.items]
            this.globalOffers.forEach(offer => lists.push(offer.items))

            lists.forEach(items => {
                items.forEach(item => {
                    if (pausedIds.indexOf(item.offer_id) !== -1) {
                        item.weight = 0
                    }
                })
            })
        },
        offerKey(offer) {
            return (offer.is_new_global_offer ? 'new-' : 'legacy-') + offer.id
        },
        offerTypeLabel(offer) {
            return offer.is_new_global_offer ? 'New Global Offer' : 'Legacy Global Offer'
        },
        offerPageHref(offer) {
            if (offer.is_new_global_offer) {
                return '/apps/smartlinks/new-global-offers/create?id=' + offer.id
            }
            return '/apps/smartlinks/global-offers-edit?id=' + offer.id + '&isVta=' + (offer.ctr_id != 0 ? 1 : 0)
        }
    }
}
</script>
